import React from 'react';
import type { ModalConfig } from '@frontend/Models/TopBar';
import BaseModal from '../modals/BaseModal';

/**
 * Renders a modal based on configuration and active modal ID
 * 
 * @param modals - Array of modal configurations
 * @param activeModalId - Currently active modal ID
 * @param closeModal - Callback to close the modal
 * @param modalData - Optional data passed to the modal
 * @returns Rendered modal component or null
 */
export const renderModal = (
  modals: ModalConfig[],
  activeModalId: string | null,
  closeModal: () => void,
  modalData?: Record<string, any>
): React.ReactElement | null => {
  if (!activeModalId) {
    return null;
  }

  // Find the modal configuration that matches the active ID
  const modalConfig = modals.find((modal) => modal.id === activeModalId);

  if (!modalConfig) {
    console.warn(`Modal configuration not found for ID: ${activeModalId}`);
    return null;
  }

  // Render custom modal component if provided
  if (modalConfig.component) {
    const CustomModal = modalConfig.component;
    return (
      <CustomModal
        isOpen={true}
        onClose={closeModal}
        config={modalConfig.config}
        modalData={modalData}
      />
    );
  }

  // Handle predefined modal types
  switch (modalConfig.type) {
    case 'export':
      // For now, use BaseModal wrapper
      // Real ExportModal integration will be in the page that uses it
      return (
        <BaseModal
          isOpen={true}
          onClose={closeModal}
          title={modalConfig.config?.title || 'Export'}
          className="export-modal"
        >
          <div>Export Modal Content (placeholder - use custom component)</div>
        </BaseModal>
      );

    case 'outlookSync':
      return (
        <BaseModal
          isOpen={true}
          onClose={closeModal}
          title={modalConfig.config?.title || 'Sync Outlook Contacts'}
          className="outlook-sync-modal"
        >
          <div>Outlook Sync Modal Content (placeholder - use custom component)</div>
        </BaseModal>
      );

    case 'custom':
      return (
        <BaseModal
          isOpen={true}
          onClose={closeModal}
          title={modalConfig.config?.title}
        >
          {modalConfig.config?.content || <div>Custom modal content</div>}
        </BaseModal>
      );

    default:
      console.warn(`Unknown modal type: ${modalConfig.type}`);
      return null;
  }
};

export default renderModal;

